import { Box, Button, Chip, Stack, Typography } from "@mui/joy";
import { motion } from "framer-motion";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import VerifiedRoundedIcon from "@mui/icons-material/VerifiedRounded";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import CalculateRoundedIcon from "@mui/icons-material/CalculateRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";

import { COLORS, GlowOrbs } from "../shared/ui";
import { useContext } from "react";
import { LayoutContext } from "../context";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const badges = [
  { icon: <BoltRoundedIcon sx={{ fontSize: 16 }} />, text: "Выезд на следующий день" },
  { icon: <VerifiedRoundedIcon sx={{ fontSize: 16 }} />, text: "Точность ±2 мм" },
  { icon: <PublicRoundedIcon sx={{ fontSize: 16 }} />, text: "150+ городов России" },
];

export default function Hero({ onShowOrder }) {
  const { setModalAuth, setQuickOrderCalcAmount, setShowQuickOrder } = useContext(LayoutContext);

  const handleQuickOrder = () => {
    setQuickOrderCalcAmount(null);
    setShowQuickOrder(true);
  };

  return (
    <Box
      component="section"
      id="hero"
      sx={{
        position: "relative",
        minHeight: { xs: "auto", md: "92vh" },
        background: `linear-gradient(135deg, ${COLORS.dark} 0%, ${COLORS.darkMid} 55%, #0f1a3a 100%)`,
        px: { xs: 3, md: 5 },
        pt: { xs: 10, md: 14 },
        pb: { xs: 8, md: 12 },
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
      }}
    >
      <GlowOrbs />

      {/* Чертёжная сетка */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          backgroundImage: `
            linear-gradient(to right, rgba(255,255,255,0.03) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(255,255,255,0.03) 1px, transparent 1px)
          `,
          backgroundSize: "48px 48px",
          maskImage: "radial-gradient(ellipse at center, #000 30%, transparent 80%)",
        }}
      />

      <Box
        sx={{
          maxWidth: 1180,
          mx: "auto",
          width: "100%",
          position: "relative",
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1.15fr 0.85fr" },
          gap: { xs: 6, md: 5 },
          alignItems: "center",
        }}
      >
        {/* ─── Левая колонка: текст ─── */}
        <Box>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Chip
              variant="outlined"
              size="sm"
              sx={{
                mb: 3,
                fontWeight: 700,
                letterSpacing: "0.14em",
                fontSize: "0.72rem",
                textTransform: "uppercase",
                borderColor: "rgba(99,102,241,0.4)",
                background: "rgba(99,102,241,0.12)",
                color: COLORS.brandLight,
              }}
            >
              Замер помещений по всей России
            </Chip>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, delay: 0.1 }}
          >
            <Typography
              level="h1"
              sx={{
                color: "#fff",
                fontWeight: 900,
                fontSize: { xs: "2.4rem", sm: "3.2rem", md: "4rem", lg: "4.4rem" },
                lineHeight: 1.02,
                letterSpacing: "-2.5px",
                mb: 3,
              }}
            >
              Обмерный план{" "}
              <Box component="span" sx={{ whiteSpace: "nowrap" }}>за 3 дня —</Box>
              <br />
              <Box
                component="span"
                sx={{
                  background: `linear-gradient(135deg, #818cf8, #c084fc)`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                или возвращаем деньги
              </Box>
            </Typography>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Typography
              level="body-lg"
              sx={{
                color: COLORS.textDim,
                maxWidth: 560,
                mb: 4,
                lineHeight: 1.65,
                fontSize: { xs: "1rem", md: "1.15rem" },
              }}
            >
              Фиксированная цена <Box component="span" sx={{ color: "#fff", fontWeight: 700 }}>150 ₽/м²</Box> — без скрытых доплат. Фото, видео и чертёж в вашей программе: AutoCAD, ArchiCAD, Revit.
            </Typography>
          </motion.div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 4 }}>
              <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                <Button
                  size="lg"
                  startDecorator={<CalculateRoundedIcon />}
                  onClick={() => scrollTo("calculator")}
                  sx={{
                    fontWeight: 800,
                    px: 4,
                    py: 1.5,
                    fontSize: "1rem",
                    borderRadius: "14px",
                    background: `linear-gradient(135deg, ${COLORS.brand}, ${COLORS.brand2})`,
                    boxShadow: "0 10px 36px rgba(99,102,241,0.5)",
                    "&:hover": {
                      boxShadow: "0 14px 48px rgba(99,102,241,0.7)",
                      background: `linear-gradient(135deg, ${COLORS.brand}, ${COLORS.brand2})`,
                    },
                  }}
                >
                  Рассчитать стоимость
                </Button>
              </motion.div>
              <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                <Button
                  size="lg"
                  variant="outlined"
                  endDecorator={<ArrowForwardRoundedIcon />}
                  onClick={handleQuickOrder}
                  sx={{
                    fontWeight: 700,
                    px: 4,
                    py: 1.5,
                    fontSize: "1rem",
                    borderRadius: "14px",
                    color: "#fff",
                    borderColor: "rgba(255,255,255,0.22)",
                    background: "rgba(255,255,255,0.04)",
                    backdropFilter: "blur(8px)",
                    "&:hover": {
                      background: "rgba(255,255,255,0.12)",
                      borderColor: "rgba(255,255,255,0.4)",
                    },
                  }}
                >
                  Оставить заявку
                </Button>
              </motion.div>
            </Stack>
          </motion.div>

          {/* Бейджи доверия */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.45 }}
          >
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
              {badges.map((b, i) => (
                <motion.div
                  key={b.text}
                  animate={{ y: [0, -3, 0] }}
                  transition={{ repeat: Infinity, duration: 3.5, delay: i * 0.4 }}
                >
                  <Stack
                    direction="row"
                    spacing={0.75}
                    alignItems="center"
                    sx={{
                      px: 1.5,
                      py: 0.75,
                      borderRadius: "999px",
                      border: "1px solid rgba(255,255,255,0.14)",
                      background: "rgba(255,255,255,0.05)",
                      backdropFilter: "blur(8px)",
                      color: COLORS.brandLight,
                    }}
                  >
                    {b.icon}
                    <Typography
                      level="body-xs"
                      sx={{ color: COLORS.textDim, fontWeight: 600 }}
                    >
                      {b.text}
                    </Typography>
                  </Stack>
                </motion.div>
              ))}
            </Stack>
          </motion.div>
        </Box>

        {/* ─── Правая колонка: декоративная чертёжная композиция ─── */}
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            justifyContent: "center",
            alignItems: "center",
            position: "relative",
            minHeight: 460,
          }}
        >
          <BlueprintCard />
        </Box>
      </Box>
    </Box>
  );
}

// ─── Декоративная «чертёжная карточка» справа от Hero ─────────────────────
function BlueprintCard() {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.92, rotate: -2 }}
      animate={{ opacity: 1, scale: 1, rotate: 0 }}
      transition={{ duration: 0.9, delay: 0.2, ease: [0.4, 0, 0.2, 1] }}
      style={{ position: "relative", width: "100%", maxWidth: 460 }}
    >
      <Box
        sx={{
          position: "relative",
          aspectRatio: "4/5",
          borderRadius: "24px",
          background: "linear-gradient(160deg, rgba(99,102,241,0.14), rgba(139,92,246,0.08))",
          border: "1px solid rgba(255,255,255,0.12)",
          backdropFilter: "blur(12px)",
          p: 3,
          overflow: "hidden",
          boxShadow: "0 30px 80px rgba(0,0,0,0.4)",
        }}
      >
        {/* Сетка миллиметровки */}
        <Box
          sx={{
            position: "absolute",
            inset: 16,
            borderRadius: "16px",
            backgroundImage: `
              linear-gradient(to right, rgba(165,180,252,0.08) 1px, transparent 1px),
              linear-gradient(to bottom, rgba(165,180,252,0.08) 1px, transparent 1px)
            `,
            backgroundSize: "24px 24px",
          }}
        />

        {/* SVG план квартиры */}
        <Box sx={{ position: "absolute", inset: 24, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <svg viewBox="0 0 300 380" width="100%" height="100%" fill="none">
            {/* Контур квартиры */}
            <motion.path
              d="M 40 60 L 260 60 L 260 180 L 220 180 L 220 240 L 260 240 L 260 340 L 40 340 Z"
              stroke="#a5b4fc"
              strokeWidth="2"
              fill="rgba(99,102,241,0.06)"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 1.6, delay: 0.6, ease: "easeInOut" }}
            />
            {/* Внутренние перегородки */}
            <motion.line
              x1="40" y1="180" x2="220" y2="180"
              stroke="#a5b4fc" strokeWidth="1.5" strokeDasharray="4 4"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 0.8, delay: 1.8 }}
            />
            <motion.line
              x1="140" y1="60" x2="140" y2="180"
              stroke="#a5b4fc" strokeWidth="1.5" strokeDasharray="4 4"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 0.8, delay: 2.0 }}
            />
            <motion.line
              x1="140" y1="240" x2="140" y2="340"
              stroke="#a5b4fc" strokeWidth="1.5" strokeDasharray="4 4"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 0.8, delay: 2.2 }}
            />

            {/* Размерная линия снизу */}
            <motion.g
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 2.4 }}
            >
              <line x1="40" y1="360" x2="260" y2="360" stroke="#c084fc" strokeWidth="1" />
              <line x1="40" y1="355" x2="40" y2="365" stroke="#c084fc" strokeWidth="1.5" />
              <line x1="260" y1="355" x2="260" y2="365" stroke="#c084fc" strokeWidth="1.5" />
              <text x="150" y="375" fill="#c084fc" fontSize="11" fontFamily="monospace" textAnchor="middle" fontWeight="700">
                8 600 мм
              </text>
            </motion.g>

            {/* Размерная линия слева */}
            <motion.g
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 2.6 }}
            >
              <line x1="20" y1="60" x2="20" y2="340" stroke="#c084fc" strokeWidth="1" />
              <line x1="15" y1="60" x2="25" y2="60" stroke="#c084fc" strokeWidth="1.5" />
              <line x1="15" y1="340" x2="25" y2="340" stroke="#c084fc" strokeWidth="1.5" />
              <text x="14" y="205" fill="#c084fc" fontSize="11" fontFamily="monospace" textAnchor="middle" fontWeight="700" transform="rotate(-90 14 205)">
                10 200
              </text>
            </motion.g>

            {/* Метки комнат */}
            <motion.g
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.7 }}
              transition={{ duration: 0.5, delay: 2.8 }}
              fill="#818cf8"
              fontSize="9"
              fontFamily="monospace"
              textAnchor="middle"
            >
              <text x="90" y="125">СПАЛЬНЯ</text>
              <text x="180" y="125">КУХНЯ</text>
              <text x="150" y="290">ГОСТИНАЯ</text>
            </motion.g>
          </svg>
        </Box>

        {/* Угловые метки */}
        {[
          { top: 12, left: 12, rotate: 0 },
          { top: 12, right: 12, rotate: 90 },
          { bottom: 12, right: 12, rotate: 180 },
          { bottom: 12, left: 12, rotate: 270 },
        ].map((pos, i) => (
          <Box
            key={i}
            sx={{
              position: "absolute",
              ...pos,
              width: 16,
              height: 16,
              borderTop: "2px solid rgba(165,180,252,0.6)",
              borderLeft: "2px solid rgba(165,180,252,0.6)",
              transform: `rotate(${pos.rotate}deg)`,
            }}
          />
        ))}

        {/* Бейдж сверху */}
        <Box
          sx={{
            position: "absolute",
            top: 20,
            right: 20,
            px: 1.5,
            py: 0.5,
            borderRadius: "6px",
            background: "rgba(99,102,241,0.2)",
            border: "1px solid rgba(99,102,241,0.4)",
            fontFamily: "monospace",
            fontSize: "0.7rem",
            fontWeight: 700,
            color: COLORS.brandLight,
          }}
        >
          M 1:50
        </Box>
      </Box>

      {/* Плавающий badge снизу */}
      <motion.div
        animate={{ y: [0, -6, 0] }}
        transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
        style={{
          position: "absolute",
          bottom: -20,
          left: -16,
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.25,
            borderRadius: "14px",
            background: "rgba(11,13,26,0.85)",
            border: "1px solid rgba(139,92,246,0.4)",
            backdropFilter: "blur(12px)",
            boxShadow: "0 16px 40px rgba(0,0,0,0.4)",
          }}
        >
          <Typography level="body-xs" sx={{ color: COLORS.textMute, fontSize: "0.65rem", letterSpacing: "0.08em" }}>
            ЗАМЕРЕНО ОБЪЕКТОВ
          </Typography>
          <Typography
            level="h3"
            sx={{
              color: "#fff",
              fontFamily: "monospace",
              fontWeight: 800,
              fontSize: "1.4rem",
              lineHeight: 1,
              mt: 0.25,
            }}
          >
            2 400+
          </Typography>
        </Box>
      </motion.div>
    </motion.div>
  );
}